// The beat sheet (SuiteModel/BeatSheet.swift, review B16) and the beat board's reads (Apps/Shared/Rooms/BeatBoard.swift,
// LibraryModel+Beats.swift): acts and beats in story order, the templates verbatim with ids `<template>.<slug>` (so two
// devices applying one merge), the Beat field kept in step, each scene's start as a fraction of the book, "near N% ·
// starts M%", and the tray of scenes without a beat.
import type { FragmentKind, MetadataValue } from '../contract.ts';
import { makeID } from '../edit/editProject.ts';
import type { FieldSpec } from '../edit/storeEditor.ts';
import { decodeJSON, lossyArray } from '../settingsJSON.ts';
import { isFarFromTarget, manuscriptStarts } from '../model/positions.ts';

export interface BeatAct { id: string; title: string }
export interface Beat {
  id: string;
  title: string;
  /** What the beat does, as a reminder while planning. */
  guidance: string;
  actID: string;
  /** Where the beat usually falls, as a fraction of the book (0–1); absent when the sheet doesn't say. */
  target?: number;
}
export interface BeatSheet {
  /** The structure it follows ("Save the Cat"). */
  name: string;
  acts: BeatAct[];
  /** Every beat, in story order. */
  beats: Beat[];
}

/** `StandardField.beat`. */
export const BEAT_FIELD = 'beat';
/** `SettingsKey.beatSheet`. */
export const BEAT_SHEET_KEY = 'beatSheet';

export const beatsIn = (sheet: BeatSheet, actID: string) => sheet.beats.filter((b) => b.actID === actID);
export const beatWithID = (sheet: BeatSheet, id: string | null | undefined) => (id ? sheet.beats.find((b) => b.id === id) ?? null : null);
export const beatIndex = (sheet: BeatSheet, id: string) => { const i = sheet.beats.findIndex((b) => b.id === id); return i >= 0 ? i : null; };

/** `BeatSheet.field`: the Beat field as it should be — an option per beat, in story order, named after it. */
export const beatField = (sheet: BeatSheet): FieldSpec => ({
  id: BEAT_FIELD, name: 'Beat', type: 'enum', options: sheet.beats.map((b) => ({ id: b.id, name: b.title })), order: 90,
});

// MARK: JSON (Codable, tolerant as `init(from:)` is)

const str = (o: Record<string, unknown>, key: string) => { const v = o[key]; if (typeof v !== 'string') throw new Error(key); return v; };

function decodeAct(raw: unknown): BeatAct | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return { id: str(o, 'id'), title: str(o, 'title') };
}

function decodeBeat(raw: unknown): Beat | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const target = o.target;
  if (target !== undefined && target !== null && (typeof target !== 'number' || !Number.isFinite(target))) return null;
  return {
    id: str(o, 'id'), title: str(o, 'title'), guidance: str(o, 'guidance'), actID: str(o, 'actID'),
    ...(typeof target === 'number' ? { target } : {}),
  };
}

/** `BeatSheet(from:)`: a name (else ""), and the acts and beats that decode. Null when it isn't an object. */
export function decodeBeatSheet(raw: unknown): BeatSheet | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  return { name: typeof o.name === 'string' ? o.name : '', acts: lossyArray(o.acts, decodeAct), beats: lossyArray(o.beats, decodeBeat) };
}

/** What `JSONEncoder` writes of a sheet (nil targets left out). */
export const encodeBeatSheet = (s: BeatSheet) => ({
  name: s.name,
  acts: s.acts.map((a) => ({ id: a.id, title: a.title })),
  beats: s.beats.map((b) => ({ id: b.id, title: b.title, guidance: b.guidance, actID: b.actID, ...(b.target !== undefined ? { target: b.target } : {}) })),
});

/** `ProjectDocument.beatSheet()`: the project's sheet, if it has one (settings as `readProject` gives them). */
export const beatSheetOf = (settings: Record<string, unknown>): BeatSheet | null => decodeJSON(settings[BEAT_SHEET_KEY], decodeBeatSheet);

// MARK: Templates

export interface BeatTemplate {
  id: string;
  name: string;
  summary: string;
  /** Acts, each with its beats: [title, guidance, target]. */
  acts: [string, [string, string, number | null][]][];
}

/** `slug`: lowercased, alphanumerics only (`CharacterSet.alphanumerics`). */
export const slug = (s: string) => [...s.toLowerCase()].filter((c) => /[\p{L}\p{M}\p{N}]/u.test(c)).join('');

/** `Template.sheet()`: ids are the template's and the beat's name, so the same template on two devices makes the same beats. */
export function sheetFromTemplate(t: BeatTemplate): BeatSheet {
  const acts: BeatAct[] = [], beats: Beat[] = [];
  t.acts.forEach(([title, list], i) => {
    const actID = `${t.id}.act${i + 1}`;
    acts.push({ id: actID, title });
    for (const [beat, guidance, target] of list) {
      beats.push({ id: `${t.id}.${slug(beat)}`, title: beat, guidance, actID, ...(target !== null ? { target } : {}) });
    }
  });
  return { name: t.name, acts, beats };
}

/** `BeatSheet.templates`, verbatim. */
export const BEAT_TEMPLATES: readonly BeatTemplate[] = [
  { id: 'save-the-cat', name: 'Save the Cat', summary: 'Fifteen beats in three acts, from Opening Image to Final Image.', acts: [
    ['Act One', [
      ['Opening Image', 'A snapshot of the hero\'s world, and their problem, before anything changes.', 0],
      ['Theme Stated', 'Someone says, often in passing, what the story will prove.', 0.05],
      ['Set-Up', 'The hero\'s life, the people in it, and what\'s missing from it.', 0.05],
      ['Catalyst', 'The event that knocks the old life off course.', 0.10],
      ['Debate', 'The hero hesitates: can they, should they?', 0.15],
    ]],
    ['Act Two', [
      ['Break into Two', 'The hero chooses to step into a new world.', 0.20],
      ['B Story', 'A new relationship arrives that carries the theme.', 0.22],
      ['Fun and Games', 'The promise of the premise: the story the reader came for.', 0.30],
      ['Midpoint', 'A false victory or a false defeat; the stakes rise.', 0.50],
      ['Bad Guys Close In', 'Pressure mounts, outside and within.', 0.62],
      ['All Is Lost', 'The lowest point; something or someone is lost.', 0.75],
      ['Dark Night of the Soul', 'The hero sits with the loss before seeing what it means.', 0.78],
    ]],
    ['Act Three', [
      ['Break into Three', 'A new idea, often from the B Story, shows the way.', 0.80],
      ['Finale', 'The hero puts the lesson into action and wins or loses for good.', 0.88],
      ['Final Image', 'The opening image answered: how the world has changed.', 0.99],
    ]],
  ] },
  { id: 'heros-journey', name: 'Hero\'s Journey', summary: 'Twelve stages: departure, initiation and return.', acts: [
    ['Departure', [
      ['Ordinary World', 'The hero at home, before the adventure.', 0],
      ['Call to Adventure', 'A problem or challenge arrives.', 0.10],
      ['Refusal of the Call', 'Fear or duty holds the hero back.', 0.15],
      ['Meeting the Mentor', 'Someone gives advice, a gift or courage.', 0.20],
      ['Crossing the Threshold', 'The hero commits and enters the unknown.', 0.25],
    ]],
    ['Initiation', [
      ['Tests, Allies, Enemies', 'The rules of the new world; who\'s friend and who\'s foe.', 0.35],
      ['Approach to the Inmost Cave', 'Preparing for the central ordeal.', 0.45],
      ['The Ordeal', 'The greatest test so far; a brush with death.', 0.50],
      ['Reward', 'Having survived, the hero takes the prize.', 0.60],
    ]],
    ['Return', [
      ['The Road Back', 'The journey home begins, often with pursuit.', 0.75],
      ['Resurrection', 'A final test that proves the change.', 0.90],
      ['Return with the Elixir', 'Home, changed, with something to share.', 0.98],
    ]],
  ] },
  { id: 'three-act', name: 'Three-Act Structure', summary: 'Setup, confrontation and resolution, with the plot points between.', acts: [
    ['Setup', [
      ['Exposition', 'The world, the hero and what they want.', 0],
      ['Inciting Incident', 'The event that sets the story moving.', 0.12],
      ['First Plot Point', 'The hero is locked into the conflict.', 0.25],
    ]],
    ['Confrontation', [
      ['Rising Action', 'Obstacles grow; attempts fail or cost.', 0.37],
      ['Midpoint', 'A turn that changes the hero\'s understanding or goal.', 0.50],
      ['Second Plot Point', 'A crisis that forces the final push.', 0.75],
    ]],
    ['Resolution', [
      ['Climax', 'The decisive confrontation.', 0.88],
      ['Resolution', 'The new normal.', 0.97],
    ]],
  ] },
  { id: 'seven-point', name: 'Seven-Point Structure', summary: 'Hook to resolution, built backwards from the ending.', acts: [
    ['Beginning', [
      ['Hook', 'The hero in a state opposite to where they\'ll end.', 0],
      ['Plot Turn 1', 'Something changes that sets the story going.', 0.15],
    ]],
    ['Middle', [
      ['Pinch 1', 'Pressure from the antagonist forces action.', 0.35],
      ['Midpoint', 'The hero moves from reacting to acting.', 0.50],
      ['Pinch 2', 'Things go wrong; the hero is on their own.', 0.62],
    ]],
    ['End', [
      ['Plot Turn 2', 'The hero finds the last piece they need.', 0.80],
      ['Resolution', 'The climax, and the hero where the story means them to be.', 0.97],
    ]],
  ] },
  { id: 'freytag', name: 'Freytag\'s Pyramid', summary: 'Five movements rising to a climax and falling away.', acts: [
    ['Rise', [
      ['Exposition', 'Who, where and what\'s at stake.', 0],
      ['Rising Action', 'Complications build towards the turn.', 0.20],
    ]],
    ['Turn', [
      ['Climax', 'The turning point, the height of the tension.', 0.50],
    ]],
    ['Fall', [
      ['Falling Action', 'The consequences unwind.', 0.70],
      ['Denouement', 'The ending; what\'s settled.', 0.92],
    ]],
  ] },
  { id: 'kishotenketsu', name: 'Kishōtenketsu', summary: 'Four movements, with a twist rather than a conflict at the heart.', acts: [
    ['Four Movements', [
      ['Ki', 'Introduction: the characters and their world.', 0],
      ['Shō', 'Development: the story deepens, without a major change.', 0.25],
      ['Ten', 'Twist: something unexpected changes how it all looks.', 0.50],
      ['Ketsu', 'Reconciliation: the parts come together in a new light.', 0.75],
    ]],
  ] },
  { id: 'blank', name: 'Your Own', summary: 'Three empty acts to fill with your own beats.', acts: [
    ['Act One', []], ['Act Two', []], ['Act Three', []],
  ] },
];

// MARK: Editing a sheet (BeatSheetEditor)

/** `addBeat(to:)`: "New Beat" after the act's last beat (else before the next act's first, else at the end). */
export function addBeat(sheet: BeatSheet, actID: string, id: string = makeID()): BeatSheet {
  const beat: Beat = { id, title: 'New Beat', guidance: '', actID };
  const beats = [...sheet.beats];
  const last = beats.map((b) => b.actID).lastIndexOf(actID);
  const actIndex = sheet.acts.findIndex((a) => a.id === actID);
  const next = actIndex < 0 ? -1 : beats.findIndex((b) => { const i = sheet.acts.findIndex((a) => a.id === b.actID); return i >= 0 && i > actIndex; });
  if (last >= 0) beats.splice(last + 1, 0, beat);
  else if (next >= 0) beats.splice(next, 0, beat);
  else beats.push(beat);
  return { ...sheet, beats };
}

/** `move(_:by:)`: a beat within its act, or across into the neighbouring act at the edge. */
export function moveBeat(sheet: BeatSheet, id: string, step: number): BeatSheet {
  const i = sheet.beats.findIndex((b) => b.id === id);
  const j = i + step;
  if (i < 0 || j < 0 || j >= sheet.beats.length) return sheet;
  const beats = sheet.beats.map((b) => ({ ...b }));
  if (beats[j].actID !== beats[i].actID) beats[i].actID = beats[j].actID;
  [beats[i], beats[j]] = [beats[j], beats[i]];
  return { ...sheet, beats };
}

/** `removeAct`: its beats join the act before (or after, for the first). */
export function removeAct(sheet: BeatSheet, actID: string): BeatSheet {
  const index = sheet.acts.findIndex((a) => a.id === actID);
  if (index < 0 || sheet.acts.length < 2) return sheet;
  const into = sheet.acts[index === 0 ? 1 : index - 1].id;
  return { ...sheet, acts: sheet.acts.filter((a) => a.id !== actID), beats: sheet.beats.map((b) => (b.actID === actID ? { ...b, actID: into } : b)) };
}

export const removeBeat = (sheet: BeatSheet, id: string): BeatSheet => ({ ...sheet, beats: sheet.beats.filter((b) => b.id !== id) });

// MARK: The board

/** `BeatScene`: a manuscript scene on the beat board. */
export interface BeatScene {
  id: string;
  title: string;
  synopsis: string;
  words: number;
  pov: string | null;
  /** The beat, when it's one the sheet knows. */
  beatID: string | null;
  /** Where it starts, as a fraction of the manuscript's words. */
  start: number;
}

/** What `beatScenes` reads of each scene. */
export interface BeatSceneSource { id: string; isFolder: boolean; title: string; synopsis: string; words: number; metadata: Record<string, MetadataValue> }

/** `beatScenes`: the manuscript's scenes in reading order (a folder only with words of its own), with where each starts
 *  and the beat it's assigned to (only a beat on `sheet`). */
export function beatScenes(scenes: BeatSceneSource[], sheet: BeatSheet | null): BeatScene[] {
  const kept = scenes.filter((s) => !s.isFolder || s.words > 0);
  const starts = manuscriptStarts(kept.map((s) => s.words));
  const known = new Set(sheet?.beats.map((b) => b.id) ?? []);
  return kept.map((s, i) => {
    const beat = s.metadata[BEAT_FIELD];
    const pov = s.metadata.pov;
    const scene: BeatScene = {
      id: s.id, title: s.title, synopsis: s.synopsis, words: s.words, pov: pov?.type === 'text' && pov.value !== '' ? pov.value : null,
      beatID: beat?.type === 'text' && known.has(beat.value) ? beat.value : null, start: starts[i],
    };
    return scene;
  });
}

/** The scene kinds beats see: the project's own (`sceneKinds`: Fountain in a screenplay). */
export const sceneKindsOf = (projectType: string): FragmentKind[] => [projectType === 'screenplay' ? 'screenplay' : 'prose'];

/** `formatted(.percent.precision(.fractionLength(0)))`: whole percent, halves to even (ICU's rounding). */
export function percent(fraction: number): string {
  const x = fraction * 100;
  let r = Math.round(x);
  if (Math.abs(x - Math.trunc(x)) === 0.5) r = Math.trunc(x) % 2 === 0 ? Math.trunc(x) : Math.trunc(x) + Math.sign(x);
  return `${r}%`;
}

/** A beat's column on the board: its scenes, where the book puts it, and the "near N% · starts M%" line (`off` when it's
 *  more than 15 points from the sheet's target once the book has 5,000 words). */
export interface BeatColumn { beat: Beat; scenes: BeatScene[]; start: number | null; label: string; off: boolean }

export function beatColumn(beat: Beat, scenes: BeatScene[], bookWords: number): BeatColumn {
  const mine = scenes.filter((s) => s.beatID === beat.id);
  const start = mine[0]?.start ?? null;
  const parts = [beat.target !== undefined ? `near ${percent(beat.target)}` : null, start !== null ? `starts ${percent(start)}` : null]
    .filter((p): p is string => p !== null);
  const off = beat.target !== undefined && start !== null && isFarFromTarget(start, beat.target, bookWords);
  return { beat, scenes: mine, start, label: parts.join(' · '), off };
}

/** The board: acts, each with its beats' columns, and the tray of scenes without a beat. */
export function beatBoard(sheet: BeatSheet, scenes: BeatScene[]): { acts: { act: BeatAct; columns: BeatColumn[] }[]; tray: BeatScene[]; bookWords: number } {
  const bookWords = scenes.reduce((n, s) => n + s.words, 0);
  return {
    acts: sheet.acts.map((act) => ({ act, columns: beatsIn(sheet, act.id).map((b) => beatColumn(b, scenes, bookWords)) })),
    tray: scenes.filter((s) => s.beatID === null),
    bookWords,
  };
}

/** `SceneDrop.scenes`: of what was dropped, only the scenes on the board (a folder dropped here is ignored). */
export const scenesOnBoard = (dropped: string[], scenes: BeatScene[]) => {
  const onBoard = new Set(scenes.map((s) => s.id));
  return dropped.filter((id) => onBoard.has(id));
};
